import React, { useEffect } from "react";
import { ElementDisplayInfo } from "../../../shared/elementDisplayInfo";
import { cellLocations } from '../../../shared/generated/assetLocations';
import { VirusGrowthMessage, VirusGrowthType, VirusMutation } from "../../../shared/messageTypes";
import { Colors } from "../palettes/colorPalette";
import { AssetLocation } from "../../../shared/utils";
import { coloredSpritesheet } from "../rendering/spriteFactory";
import { BoldText } from "./keywordText";
import { GameClient } from "../gameClient";
import { url } from "inspector";
import { backgroundTexture } from "../uiConstants";
import { RewardDisplay } from "./RewardDisplay";

type MutationModelProps = {
	change: VirusGrowthMessage[]
};

declare var gameClient: GameClient;

const modelStyle: React.CSSProperties = {
	position: "absolute",
	color: Colors.UITextAndBorders,
	width: "30vw",
	paddingBottom: "2vh",
	background: `url(${backgroundTexture})`,
	backgroundColor: Colors.UIBackground,
	textShadow: "none",
	textAlign: "center",
	borderRadius: "10pt",
	border: `solid 3px ${Colors.UITextAndBorders}`,
	top: "12%",
	left: "32%",
	paddingLeft: "10px",
	paddingRight: "10px",
	fontSize: "1vw"
}

const graphicStyle = {
	display: "flex",
	marginLeft: "auto",
	marginRight: "auto",
	width: "max-content",
};

const needsE = (letter: string) => {
	return
}

const simplePlural = (name: string) => {
	const lastLetter = name.charAt(name.length - 1)
	if ((/^[sxs]$/i).test(lastLetter)) {
		return name + 'es';
	} else {
		return name + 's';
	}
}

export const MutationModel = (props: MutationModelProps) => {
	useEffect(() => {
		// const keylistener = (event: KeyboardEvent) => {
		// 	gameClient.onConfirmVirusGrowth();
		// }
		const mouselistener = (event: MouseEvent) => {
			gameClient.onConfirmVirusGrowth();
		}

		//document.addEventListener('keydown', keylistener);
		document.addEventListener('click', mouselistener);
		return () => {
			//document.removeEventListener('keydown', keylistener);
			document.removeEventListener('click', mouselistener);
		};
	})

	return <div>{props.change.map((growth, i) => {
		const displayInfo = ElementDisplayInfo.get(growth.type)!;
		const pluralName = simplePlural(displayInfo.displayName);
		const assetLocation = (cellLocations as any)[displayInfo.asset] as AssetLocation;
		if (!assetLocation) {
			throw new Error("Missing virus color asset in mutation model: " + displayInfo.asset);
		}

		const buttonImageSize = 64;
		const spritesheet = coloredSpritesheet;
		const scaleFactor = buttonImageSize / Math.max(assetLocation.width, assetLocation.height);
		const bgSize = spritesheet.width * scaleFactor;

		const VirusIcon = (location: AssetLocation) => (<div style={{
			backgroundImage: `url(${spritesheet.src})`,
			backgroundPosition: `${location.x * scaleFactor}px ${-location.y * scaleFactor}px`,
			backgroundSize: `${bgSize}px`,
			display: "inline-block",
			width: `${location.width * scaleFactor}px`,
			height: `${location.height * scaleFactor}px`,
		}}></div>);

		const Arrow = () => (
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 100" style={{
				width: `${buttonImageSize}`,
				height: `${buttonImageSize}`,
				paddingLeft: "10px",
				paddingRight: "10px"
			}}>
				<defs>
					<marker id="arrowhead" markerWidth="10" markerHeight="7"
						refX="0" refY="3.5" orient="auto">
						<polygon points="0 0, 10 3.5, 0 7" />
					</marker>
				</defs>
				<line x1="0" y1="50" x2="75" y2="50" stroke="#000"
					strokeWidth="8" markerEnd="url(#arrowhead)" />
			</svg>
		);

		if (growth.growthType === VirusGrowthType.Mutation) {
			const mutation = growth as VirusMutation;
			const toDisplayInfo = ElementDisplayInfo.get(mutation.toType)!;
			const toPluralName = simplePlural(toDisplayInfo.displayName);
			const toAssetLocation = (cellLocations as any)[toDisplayInfo.asset] as AssetLocation;
			if (!toAssetLocation) {
				throw new Error("Missing virus color asset in mutation model: " + displayInfo.asset);
			}
			return (
				<div id="mutation-modal" style={modelStyle} key={i}>
					<h2>Mutation!</h2>
					<div className="mutation-graphic" style={graphicStyle}>
						{VirusIcon(assetLocation)}
						{Arrow()}
						{VirusIcon(toAssetLocation)}
					</div>
					<p />
					{` ${pluralName} have mutated into ${toPluralName}!`}
				</div >
			)
		}

		else if (growth.growthType === VirusGrowthType.Spawn) {
			return (
				<div id="mutation-modal" style={modelStyle} key={i}>
					<h2>New Virus Wave!</h2>
					<div className="mutation-graphic" style={graphicStyle}>
						{VirusIcon(assetLocation)}<div style={{
							fontWeight: 'bold',
							fontSize: `1.5vw`,
							marginTop: "auto",
							paddingLeft: "0.5vw"
						}}>x {growth.increase}</div>
					</div>
					<p />
					{BoldText('' + growth.increase)} {` new infected cells carrying `} {BoldText(` ${pluralName}`)} {` have appeared!`}
					{gameClient.showVirusGrowthDisplay && <div style={{ color: Colors.UIUnemphasizedText, marginTop: "0.5vh", fontSize: "0.8em" }}>Click to proceed</div>}
				</div >
			)
		}
		else {
			return (
				<div id="mutation-modal" style={modelStyle} key={i}>
					<h2>Viral Load Increase!</h2>
					<div className="mutation-graphic" style={graphicStyle}>
						{VirusIcon(assetLocation)} <div style={{
							fontWeight: 'bold',
							fontSize: `1.5vw`,
							margin: "auto",
							paddingLeft: "0.5vw"
						}}>+ {growth.increase}</div>
					</div>
					<p />
					{BoldText(`${displayInfo.displayName}`)} {`count increased by `} {BoldText(`${growth.increase}`)} {`!`}
					{gameClient.showVirusGrowthDisplay && <div style={{ color: Colors.UIUnemphasizedText, marginTop: "0.5vh", fontSize: "0.8em" }}>Click to proceed</div>}
				</div >
			)
		}
	})}</div>;
}